import React, { useRef } from "react";
import { Divider, Typography } from "@mui/material";
import "react-medium-image-zoom/dist/styles.css";
import "../JaldiLab.css";
import {
  getCircleTypeListItem,
  getTextContentHeading,
  h1style,
  h2style,
  h3style,
  paraStyle,
  referenceTootKit,
  sectionStyle,
  subHeadingContent,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import user from "../../../assets/SvgIcons/users.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import evidenceLocker from "../../../assets/Images/Evidence_locker.jpg";
import evidenceStorage from "../../../assets/Images/Evidence_Storage_Room.jpg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import malkhanaImg from "../../../assets/Images/EvidenceRoom Images/Malkhana.png";
import { FigureDesc65, ImageDesc68 } from "../../../utils/tmp/ImageDescription/EvidenceRoomID";

const referenceStyle = {
  color: "black",
  fontFamily: "GT Super Text",
  fontSize: " 24px",
  fontWeight: "700",
  letterSpacing: "0px",
  textAlign: "left",
  fontStyle: "italic",
  cursor: "pointer",
  marginBottom: "10px",
};

const EvidenceRoom = ({ goToSection }) => {
  const referencesRef = useRef(null);

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };
  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceHtml = (stringsArray) => {
    return (
      <ol>
        {stringsArray.map((text, index) => (
          <li style={{ ...pointStyle, paddingLeft: "10px" }} key={text.content}>
            {text.content}
            {text?.link && (
              <a
                style={{
                  color: "#DC002D",
                  textDecoration: "none",
                }}
                href={text.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                {text.description}
              </a>
            )}
          </li>
        ))}
      </ol>
    );
  };

  const referenceData = [
    {
      content:
        <>Bureau of Police Research and Development, <i>Project Report on the Malkhana Management System: 2020-21</i> (Ministry of Home Affairs, Government of India, 14 September 2020) </>,
      link: "https://www.thedailystar.net/law-our-rights/news/need-proper-malkhana-management-criminal-justice-system-2204266",
      description: <>
        &lt;https://bprd.nic.in/WriteReadData/userfiles/file/202010211235100597064MalkhanaManagementSystemProject(MM04).pdf&gt; [“Malkhana Management System Report”]; ‘Need for Proper Malkhana Management in Criminal Justice System’ (<i>The Daily Star,</i> 23 October 2021) &lt;https://www.thedailystar.net/law-our-rights/news/need-proper-malkhana-management-criminal-justice-system-2204266&gt;
      </>,
    },
    {
      content: "Malkhana Management System Report.",
    },
    {
      content:
        <>Steve Berdrow, ‘From the Pages of The Evidence Log: Property Room Design’ <i>(International Association for Property and Evidence, Inc.)</i> </>,
      link: "https://www.iape.org/Old_Stuff/ELarticles/proproomdesign.html",
      description:
        "<https://www.iape.org/Old_Stuff/ELarticles/proproomdesign.html>",
    },
  ];

  const evidencePropertObject = {
    data: [
      "Biological evidence;",
      "Arms, ammunition, and military equipment;",
      "Oversized objects of evidence;",
      "Valuables, jewellery, money, etc.;",
      "Drugs/narcotics seized in the course of investigation;",
      "Other evidence(paper, photographs, and other smaller objects);",
      "Bio-hazards, hazardous materials or flammable items;",
      "Digital evidence in electrostatic bags with label and tagging; and",
      "Stationery and storage boxes/covers, etc.",
    ],
    isOrdered: false,
  };

  const OjectiveObject = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        Ensure that the Evidence Room in a District Court is a{" "}
        <span style={{ color: "#DC002D" }}>
          highly secure and in a restricted area{" "}
        </span>{" "}
        where only authorised personnel are permitted to enter the room;
      </>,

      <>
        <span style={{ color: "#DC002D" }}>
          Provide adequate secure storage{" "}
        </span>
        to ensure that the original property/evidence is not tampered with;
      </>,

      <>
        Provide{" "}
        <span style={{ color: "#DC002D" }}>
          facilities to process, preserve and carefully record and catalogue the
          articles/evidence with labels{" "}
        </span>{" "}
        (digital records, photographs, and paper evidence) in a systematic and
        appropriate manner for easy retrieval;{" "}
      </>,

      <>
        Ensure that the Evidence Room is{" "}
        <span style={{ color: "#DC002D" }}>
          kept free of pests and rodents and other threats{" "}
        </span>{" "}
        that could damage the evidence, by carrying out rodent control treatment
        every 20 days;{reference(referenceData[1], 2)} and{" "}
      </>,

      <>
        Protect the evidence inside the Evidence Room from spoiling in any
        manner by{" "}
        <span style={{ color: "#DC002D" }}>
          avoiding temperature fluctuation, keeping it moisture-free, and
          maintaining a fireproof and well-ventilated space{" "}
        </span>{" "}
        at all times.{" "}
      </>,
    ],
    isOrdered: true,
    isImage: true
  };

  const usersObject = {
    icon: <img src={user} />,
    heading: "Users",
    data: [
      "Security staff",
      "Police Inspectors (PI) and accompanying police personnel",
      "Staff and Technicians at the Evidence Room",
    ],
    isOrdered: true,
    isImage: true
  };

  const adjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      <>
        The Evidence Room must be located away from main circulation paths and
        the public zones of the building. (See:
        <span
          onClick={() =>
            goToSection("spacePlanningCirculation", "horizonatalCir")
          }
          style={sectionStyle}
        >
          {" "}
          Sections 1.4 and 1.5
        </span>
        )
      </>,

      <>
        It must be located adjacent to or in close proximity to the Police
        Inspectors’ Room (
        <span
          onClick={() =>
            goToSection("policeInspectorsRoom", "headingWaitingAreas")
          }
          style={sectionStyle}
        >
          Section 2.7.C
        </span>
        ).
      </>,

      <>
        It must have access to secure corridors or entry from the outside, i.e.,
        from site entrance and building entrance (
        <span
          onClick={() => goToSection("/", "state-of")}
          style={sectionStyle}
        >
          Section 1.2
        </span>
        ) for the police officers to bring the evidence directly to the Evidence
        Room.
      </>,
    ],
    isOrdered: true,
    isImage: true
  };

  const nestedListItem = [
    "Storage shelves/compactor systems for general paper evidence and small objects.",
    "Lockers/vaults to store valuable evidence such as money, jewellery and other valuables.",
    "Room to store oversized evidence or flammable evidence.",
    "Refrigerator or cooler to store biological evidence.",
    "Separate room for the storage of guns, money and narcotics with additional security measures, and preferably with solid concrete block walls.",
    "A fire-proof metal cabinet for flammables and explosives to ensure minimal damage.",
    "A space for handling and storing electronic evidence.",
  ];

  const designConsiderationsObject = [
    {
      icon: <img src={designConsideration} />,
      heading: "Design Considerations",
      items: [
        <>
          The Evidence Room must be designed to ensure that there is adequate
          space to segregate and store different kinds of evidence, through the
          following:
          {getCircleTypeListItem(nestedListItem, true)}
        </>,

        "A built-in freezer or free standing units may be used to store biological evidence, depending on the space available within the Evidence Room. A built-in freezer may be preferred for a new construction or new remodels, whereas free standing units may be preferred for retrofitting purposes.",

        "Storage shelves may be provided till the ceiling, with provision of ladders to access the higher shelves.",

        "A Compact Storage System must be used which consists of compactors, used to store voluminous records on a limited floor area and to keep them free from dust or pest problems. A clear digitised cataloguing system must be maintained for easy retrieval of evidence when needed. Automated Identification and Data Capture Technologies such as Bar Code, QR Code, RFID, etc., may be used whenever possible.  ",

        "The height and width of the storage shelves must be adjustable to have greater versatility in storage capabilities.",

        "The walls of the Evidence Room must not be partition walls, but must be strong load-bearing walls which go up to the ceiling, to ensure protection from break-ins.",

        "Wherever possible, a separate room within the Evidence Room of each District Court must be set aside for the deposit of arms, ammunition, and military stores. This separate room must be kept under double-lock and accessed with the supervision of the staff responsible for maintaining the Evidence Room. ",

        <>
          Ventilation through a continuously-running, separate exhaust fan must be
          provided in the Evidence Room in the areas marked out for storage of
          narcotic drugs and evidence.{reference(referenceData[2], 3)} Ventilators
          (if provided) must be made secure.{" "}
        </>,

        "The Evidence Room must be equipped with adequate technological requirements including a computer system with adequate access to internet facilities, scanners, photocopying facilities, and printers.",

        "One-way pass through lockers must be provided between the storage space and the office space provided for the staff. ",

        "Strict access controls and monitoring mechanisms must be implemented to safeguard the integrity of evidence being stored in the Evidence Room.",

        <>
          All areas within the Evidence Room must have CCTV cameras which may be
          monitored through a video screen in the office. The CCTV feed must be
          collected into the Command & Control Centre (
          <span
            onClick={() =>
              goToSection("officeSpaces", "officeSpacesMultiPersonWorkspace")
            }
            style={sectionStyle}
          >
            Section 2.5.C.
          </span>
          ) and only accessed via the Video Management Systems put in place. For
          further details, please refer to{" "}
          <span
            onClick={() =>
              goToSection("managementPoliciesStrategies", "security")
            }
            style={sectionStyle}
          >
            PART D, Section 4.1.
          </span>{" "}
        </>,
      ]
    },
    {
      heading: "Entrance to the Evidence Room",
      items: [
        "All entrances to the Evidence Room must have a double lock or two-factor authentication doors. The Record Room staff must be assigned with Biometric Access Control Cards. ",

        "All doors and door frames must be made of metal.",

        "All door hinges must be designed in a manner such that the hinge pins cannot be removed.",

        "The locks used must be of good quality, to ensure that keys are not easily duplicated. Wherever possible, a computerised card-key system may be preferred. ",

      ]
    },
    {
      heading: "Office Space for Evidence Room Staff",
      items: [
        "A separate attached office space must be provided for the staff at the Evidence Room. ",

        "The office must have adequate space for desks and chairs for technicians/staff who receive the evidence submitted by the police officers, and lockers and benches to store the evidence before it is catalogued and stored appropriately.",

        "Adequate space (desk surface) must be provided for processing the evidence i.e., marking the evidence and scanning to digitise it, wherever necessary. ",

      ]
    },
  ]

  const pointStyle = {
    fontFamily: "Lato",
    fontSize: "18px",
    fontWeight: "400",
    lineHeight: "27px",
    color: "#555555",
    letterSpacing: "0em",
    textAlign: "justified",
    margin: "10px 30px",
  };

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART C - THE HANDBOOK
        </h1>{" "}
        <h2 id="HeadingChapterTwo" style={h2style}>
          <span style={{ fontSize: "24px" }}>2. </span>
          ORGANIZATION AND DESIGN OF SPACES OF THE COURT COMPLEX
        </h2>
        <h3 id="leagalFacilities" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.9. </span>
          Legal Facilities
        </h3>
        <h3 id="HeadingEvidenceRoom" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.9.</span>D. Evidence Room
        </h3>
        <p style={paraStyle}>
          The 'property room' or 'evidence room' in a Court Complex is commonly
          known as a Malkhana or Strong Room in most jurisdictions. The space is
          often used to store valuable evidence in ongoing as well as disposed
          cases. It is the designated place to store the property seized by the
          police during the course of investigation under the Code of Criminal
          Procedure (1973).{" "}
        </p>
        <p style={paraStyle}>
          The evidence room may be used to store the following types of evidence
          or property:{reference(referenceData[0], 1)}
        </p>
        {getTextContentHeading(evidencePropertObject)}
        {getTextContentHeading(OjectiveObject)}
        {getTextContentHeading(usersObject)}
        {getTextContentHeading(adjacenciesObject)}
        {subHeadingContent(designConsiderationsObject)}

        <div style={{ paddingTop: "15px" }}>
          <CaptionWithBorders1
            image={[evidenceLocker, evidenceStorage]}
            caption={
              " Image 68: Examples of pass-through lockers, refrigerators, and Compact Storage Systems for storing evidence at the Bellevue Police Department, Washington (US) and the Oswego Police Department, New York (US)"
            }
            creditLabel="Source"
            imageCredit="spacesaver.widen.net"
            height="400px"
            captionPadding="35px"
            imageDescription={ImageDesc68}
          />
        </div>
        <CaptionWithBorders1
          image={[malkhanaImg]}
          isZoom={true}
          caption={"Figure 65: Typical floor plan of an Evidence Room"}
          zoomImgStyle={{ height: "580px" }}
          imageDescription={FigureDesc65}
        />
        <h2 ref={referencesRef} id="evidenceRef" style={referenceStyle}>
          References
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
};

export default EvidenceRoom;
